import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { collection, getDocs, limit as fsLimit, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { tl } from "../lib/format";
import { Spinner } from "./Spinner";

/**
 * KATALOGDAN ÜRÜN SEÇİCİ
 *
 * NİÇİN: SSH ve stok siparişi formları ürünü "Ürün ID" adında serbest metin
 * kutusuna yazdırıyordu. Kimse Shopify varyant kimliğini ezbere bilmiyor;
 * yanlış ya da boş kimlik girilince kayıt katalogdaki hiçbir ürüne bağlanmıyor,
 * reçete bulunamıyor ve kesim listesi boş çıkıyordu — Firestore hata vermeden
 * boş döndüğü için hata da görünmüyordu.
 *
 * Bu bileşen ürünü KATALOGDAN seçtirir; dışarıya hem kimliği hem adı hem de
 * SATIŞ FİYATINI verir, o yüzden çağıran taraf bunları ayrı ayrı doğru okumak
 * zorunda kalmaz — yanlış alan adıyla okuma riski tek yerde toplanır.
 *
 * ARAMA: ürün adı `searchTokens` dizisinde normalize edilmiş olarak duruyor
 * (Türkçe katlanmış, ASCII karşılığı da var). Ön ek belirteçleri de yazıldığı
 * için "kit" yazınca "Kitaplık" bulunur. Bu yüzden burada ayrıca istemci tarafı
 * süzme yapılmaz — sorgu tek başına yeterlidir.
 *
 * KIRPILMA HATASI (düzeltildi): açılır liste `absolute` konumlanıyordu. Bu
 * bileşen "Yeni Stok Siparişi" penceresinin İÇİNDE kullanılıyor ve pencere
 * gövdesi artık `overflow-y-auto` bir kaydırma kabı (`.modal-body`). Kaydırma
 * kabı, taşan mutlak konumlu çocuğu KIRPAR — liste pencerenin alt kenarında
 * kesiliyor, alttaki ürünler seçilemiyordu. Telefonda giriş alanı zaten dibe
 * yakın olduğu için liste neredeyse tümüyle görünmez oluyordu.
 *
 * ÇÖZÜM: liste `document.body`ye portal ile taşınır ve `position: fixed` ile
 * giriş kutusunun altına hizalanır — hiçbir kaydırma kabı onu kırpamaz. Yer
 * yetmezse yukarı açılır; yükseklik her zaman görünür alanla sınırlanır.
 * Sayfa/pencere kaydırılınca veya klavye açılınca konum yeniden hesaplanır.
 */

/** Açılır listenin ekrandaki yeri — `position: fixed` değerleri. */
interface DropPos {
  left: number;
  width: number;
  /** Yukarı açılıyorsa `bottom`, aşağı açılıyorsa `top` kullanılır. */
  top?: number;
  bottom?: number;
  maxHeight: number;
}

/** Liste ile giriş kutusu arası boşluk ve ekran kenarı payı (px). */
const GAP = 4;
const EDGE = 8;

export interface PickedProduct {
  id: string;
  name: string;
  sku: string;
  imageUrl: string;
  /**
   * ★ KATALOG SATIŞ FİYATI — KURUŞ, TAM SAYI. Fiyatı olmayan/okunamayan üründe 0.
   *
   * NİÇİN EKLENDİ: manuel sipariş ekranında katalogdan ürün seçilince "Birim
   * fiyat (KDV dahil)" kutusu BOŞ geliyor ve satır toplamı ₺0,00 kalıyordu.
   * Sebep basit ama görünmezdi: bu seçici ürün dokümanından yalnızca
   * `name` / `sku` / `imageUrl` / `stockPolicy` okuyordu — FİYAT ALANINI HİÇ
   * OKUMUYORDU. Fiyat veride vardı (ölçüm: 516/516 üründe `price` dolu ve
   * tam sayı), ekran onu hiç sormuyordu. Firestore olmayan bir alanı sormadığı
   * için hata da vermiyordu; klasik sessiz arıza.
   *
   * ⚠ ALAN ADI `price` — `priceKurus` ya da `salePrice` DEĞİL. Ölçüm
   * (`products` koleksiyonu, 516 doküman): `price` alanı 516/516 dokümanda var,
   * 516/516'sı tam sayı ve sıfırdan büyük; örnek `price = 599000` → 5.990,00 TL.
   * Birim KURUŞ'tur. Bu yüzden burada `/100` YAPILMAZ; kuruş, kuruş olarak
   * taşınır ve TL'ye çevrim yalnızca ekranda (`formatKurusInput`) yapılır.
   * Birimi ad taşısın diye alan adı `priceKurus` seçildi — "aynı alanda iki
   * birim" bu projenin en pahalı hata sınıfı.
   */
  priceKurus: number;
}

interface ProductRow extends PickedProduct {
  stockPolicy: string;
}

/**
 * Katalogdaki `price` alanını güvenli kuruş tam sayısına çevirir.
 *
 * Bozuk/eksik değer 0 döner (null değil): fiyat kutusu boş kalır, kullanıcı
 * elle yazar. Sessizce küsuratlı bir sayı taşımak sunucudaki
 * `unitPriceKurus: z.number().int()` doğrulamasına takılır ve kullanıcı
 * "Geçersiz girdi." dışında hiçbir şey göremezdi.
 */
function toPriceKurus(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n);
}

/** Sunucudaki `core/searchKeys.ts` ile aynı katlama — sorgu anahtarı üretir. */
function foldQuery(raw: string): string {
  return raw
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    // En uzun kelime en seçicidir; `array-contains` tek değer alır.
    .sort((a, b) => b.length - a.length)[0] ?? "";
}

export function ProductPicker({
  value,
  onChange,
  label = "Ürün",
  required = true,
}: {
  value: PickedProduct | null;
  onChange: (p: PickedProduct | null) => void;
  label?: string;
  required?: boolean;
}) {
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<DropPos | null>(null);
  // Sabit `id` yerine useId: aynı ekranda iki seçici olursa kimlikler
  // çakışmaz ve `htmlFor`/`aria-controls` yanlış öğeyi göstermez.
  const inputId = useId();
  const listId = useId();

  const key = useMemo(() => foldQuery(term), [term]);

  const listOpen = open && key.length >= 2;

  // Giriş kutusunun ekrandaki yerinden listenin `fixed` konumunu hesaplar.
  // Görsel viewport kullanılır: yazılım klavyesi açılınca gerçek görünür alan
  // budur, `innerHeight` klavyenin altını da sayar ve liste klavyenin arkasında
  // kalırdı.
  const place = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vv = window.visualViewport;
    const vTop = vv?.offsetTop ?? 0;
    const vLeft = vv?.offsetLeft ?? 0;
    const vH = vv?.height ?? window.innerHeight;
    const vW = vv?.width ?? window.innerWidth;

    const below = vTop + vH - r.bottom - GAP - EDGE;
    const above = r.top - vTop - GAP - EDGE;
    // Yukarıda belirgin biçimde daha çok yer varsa yukarı aç.
    const up = below < 160 && above > below;

    const width = Math.min(r.width, vW - 2 * EDGE);
    const left = Math.min(Math.max(r.left, vLeft + EDGE), vLeft + vW - width - EDGE);

    setPos(
      up
        ? {
            left,
            width,
            bottom: Math.max(0, window.innerHeight - r.top + GAP),
            maxHeight: Math.max(120, Math.min(288, above)),
          }
        : {
            left,
            width,
            top: r.bottom + GAP,
            maxHeight: Math.max(120, Math.min(288, below)),
          },
    );
  }, []);

  // Açılışta ölç; sonra sayfa/pencere kaydırma, yeniden boyutlanma ve klavye
  // açılıp kapanmasında yeniden ölç. `capture: true` — pencere gövdesi gibi İÇ
  // kaydırma kaplarının olayları da yakalanır, yoksa liste giriş kutusundan
  // ayrı düşerdi.
  useLayoutEffect(() => {
    if (!listOpen) {
      setPos(null);
      return;
    }
    place();
    const vv = window.visualViewport;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    vv?.addEventListener("resize", place);
    vv?.addEventListener("scroll", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      vv?.removeEventListener("resize", place);
      vv?.removeEventListener("scroll", place);
    };
  }, [listOpen, place]);

  // Satır sayısı değişince liste boyu değişir — konumu tazele.
  useLayoutEffect(() => {
    if (listOpen) place();
  }, [rows, busy, error, listOpen, place]);

  useEffect(() => {
    if (key.length < 2) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setBusy(true);
    setError("");

    const t = setTimeout(async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "products"),
            where("deleted", "==", false),
            where("searchTokens", "array-contains", key),
            fsLimit(25),
          ),
        );
        if (cancelled) return;
        setRows(
          snap.docs.map((d) => ({
            id: d.id,
            name: String(d.get("name") ?? d.get("title") ?? d.id),
            sku: String(d.get("sku") ?? ""),
            imageUrl: String(d.get("imageUrl") ?? ""),
            // ★ Fiyat da SEÇİMLE BİRLİKTE gelir; çağıran taraf ikinci bir okuma
            //   yapmak zorunda kalmasın (yapsaydı yine yanlış alan adı riski).
            priceKurus: toPriceKurus(d.get("price")),
            stockPolicy: String(d.get("stockPolicy") ?? "madeToOrder"),
          })),
        );
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Ürün aranamadı.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [key]);

  // Dışarı tıklayınca liste kapanır — seçim yapılmadan form gönderilirse
  // kullanıcı hangi ürünün seçili olduğunu göremezdi.
  // Liste artık portal ile `body`ye taşındığı için `boxRef` onu İÇERMEZ; liste
  // kabı ayrıca kontrol edilmezse listeye dokunmak listeyi kapatıyor, hiçbir
  // ürün seçilemiyordu.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (boxRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  if (value) {
    return (
      <div>
        <span className="block text-sm font-medium text-gray-700">{label}</span>
        <div className="mt-1 flex items-center gap-3 rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
          {value.imageUrl ? (
            <img src={value.imageUrl} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
          ) : (
            <div className="h-10 w-10 shrink-0 rounded bg-gray-200" />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{value.name}</p>
            <p className="truncate text-xs text-gray-600">
              {value.sku ? `SKU ${value.sku} · ` : ""}
              {value.id}
            </p>
          </div>
          <button
            type="button"
            /* min-h/min-w 44px: tablette dokunma hedefi. Eskiden yalnız metin
               yüksekliği kadardı (~20px) ve parmakla ıskalanıyordu. */
            className="-mr-2 inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center px-2 text-sm text-blue-700 underline"
            onClick={() => {
              onChange(null);
              setTerm("");
              setOpen(true);
            }}
          >
            Değiştir
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <label className="block text-sm font-medium text-gray-700" htmlFor={inputId}>
        {label} {required ? <span className="text-red-600">*</span> : null}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        /* min-h-[44px]: atölye tableti — parmakla isabet edecek yükseklik. */
        className="input mt-1 min-h-[44px]"
        placeholder="Ürün adı ya da SKU ile ara…"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          // Esc önce LİSTEYİ kapatsın. Durdurulmazsa olay `document`e ulaşır ve
          // Modal'ın Esc dinleyicisi bütün pencereyi kapatır — kullanıcı formu
          // kaybederdi.
          if (e.key === "Escape" && listOpen) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
        autoComplete="off"
        role="combobox"
        aria-expanded={listOpen}
        aria-controls={listId}
      />

      {/* Liste `body`ye portal edilir: pencere gövdesinin kaydırma kabı onu
          kırpamaz. Konum `pos` içinde `fixed` olarak hesaplanır. */}
      {listOpen && pos
        ? createPortal(
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              className="noprint fixed z-[70] overflow-y-auto overscroll-contain rounded-md border border-gray-300 bg-white shadow-lg"
              style={{
                left: pos.left,
                width: pos.width,
                ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
                maxHeight: pos.maxHeight,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {busy ? (
                <div className="px-3 py-3">
                  <Spinner size="sm" label="Aranıyor" />
                </div>
              ) : error ? (
                <p className="px-3 py-3 text-sm text-red-700">{error}</p>
              ) : rows.length === 0 ? (
                <p className="px-3 py-3 text-sm text-gray-600">
                  Eşleşen ürün yok. Ürün adının bir kelimesini yazmayı deneyin.
                </p>
              ) : (
                <ul>
                  {rows.map((r) => (
                    <li key={r.id} role="option" aria-selected="false">
                  <button
                    type="button"
                    className="flex min-h-[44px] w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
                    onClick={() => {
                      onChange({
                        id: r.id,
                        name: r.name,
                        sku: r.sku,
                        imageUrl: r.imageUrl,
                        priceKurus: r.priceKurus,
                      });
                      setOpen(false);
                    }}
                  >
                    {r.imageUrl ? (
                      <img src={r.imageUrl} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />
                    ) : (
                      <div className="h-9 w-9 shrink-0 rounded bg-gray-200" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-gray-900">{r.name}</span>
                      {/* Fiyat listede de görünür: seçmeden önce doğru ürünü
                          seçtiğini teyit etmenin en hızlı yolu. */}
                      <span className="block truncate text-xs text-gray-600">
                        {r.sku ? `SKU ${r.sku}` : r.id}
                        {r.priceKurus > 0 ? ` · ${tl(r.priceKurus)}` : ""}
                        {r.stockPolicy === "madeToStock" ? " · stoktan (1 iş günü)" : ""}
                      </span>
                    </span>
                  </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>,
            document.body,
          )
        : null}

      {term && key.length < 2 ? (
        <p className="mt-1 text-xs text-gray-600">Aramak için en az 2 harf yazın.</p>
      ) : null}
    </div>
  );
}
